import type { ConsentUiTheme } from "./types";

const FALLBACK_FONT =
  'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
type Rgb = [number, number, number];

// Only opaque, concrete colors cross the host boundary. No CSS variables,
// URLs, alpha, or arbitrary declarations can alter the isolated component.
function parseColor(value: unknown): Rgb | undefined {
  if (typeof value !== "string") return;
  const hex = /^#([\da-f]{3}|[\da-f]{6})$/i.exec(value.trim());
  if (hex) {
    const full =
      hex[1].length === 3 ? [...hex[1]].map((x) => x + x).join("") : hex[1];
    return [0, 2, 4].map((offset) =>
      parseInt(full.slice(offset, offset + 2), 16),
    ) as Rgb;
  }
  const rgb =
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*1(?:\.0*)?)?\s*\)$/.exec(
      value.trim(),
    );
  if (!rgb) return;
  const channels = rgb.slice(1, 4).map(Number) as Rgb;
  return channels.every((x) => x <= 255) ? channels : undefined;
}

function luminance(rgb: Rgb) {
  const linear = rgb.map((x) => {
    const value = x / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722;
}

function contrast(a: Rgb, b: Rgb) {
  const l1 = luminance(a),
    l2 = luminance(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

export function resolveConsentTheme(
  theme: ConsentUiTheme = {},
  host: ConsentUiTheme = {},
) {
  theme = theme && typeof theme === "object" ? theme : {};
  host = host && typeof host === "object" ? host : {};
  const background: Rgb = parseColor(theme.backgroundColor) ??
    parseColor(host.backgroundColor) ?? [255, 255, 255];
  const fallback: Rgb =
    contrast(background, [0, 0, 0]) >= contrast(background, [255, 255, 255])
      ? [0, 0, 0]
      : [255, 255, 255];
  const requestedText =
    parseColor(theme.textColor) ?? parseColor(host.textColor);
  const text =
    requestedText && contrast(background, requestedText) >= 4.5
      ? requestedText
      : fallback;
  const requestedAccent = parseColor(theme.accentColor);
  const accent =
    requestedAccent && contrast(background, requestedAccent) >= 4.5
      ? requestedAccent
      : text;
  const requestedFont = theme.fontFamily ?? host.fontFamily;
  const font =
    typeof requestedFont === "string" &&
    requestedFont.length <= 200 &&
    /^[\w\s,"'-]+$/.test(requestedFont)
      ? requestedFont
      : FALLBACK_FONT;
  const css = (rgb: number[]) => `rgb(${rgb.join(", ")})`;
  return {
    backgroundColor: css(background),
    textColor: css(text),
    accentColor: css(accent),
    fontFamily: font,
  };
}

export function readConsentHostTheme(): ConsentUiTheme {
  try {
    const body = getComputedStyle(document.body);
    const html = getComputedStyle(document.documentElement);
    return {
      fontFamily: body.fontFamily,
      textColor: body.color,
      backgroundColor: parseColor(body.backgroundColor)
        ? body.backgroundColor
        : html.backgroundColor,
    };
  } catch {
    return {};
  }
}
